import type { PostHogCallbackContext, PostHogContextRef } from '../context'
import { buildInChatContextProperties } from '../context'
import { setAiGa4Event } from '../../../../services/event-tracking/actions'
import {
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_CONVERSATION_STARTED_GA4_EVENT,
  AI_CONVERSATION_RESUMED_GA4_EVENT,
  AI_CONVERSATION_OPENED_GA4_EVENT,
  AI_CONVERSATION_DELETED_GA4_EVENT,
  AI_CONVERSATION_FAVORITED_GA4_EVENT,
  AI_CONVERSATION_UNFAVORITED_GA4_EVENT,
  AI_CONVERSATION_NAME_UPDATED_GA4_EVENT,
  AI_CANCEL_MODAL_VIEWED_GA4_EVENT,
  AI_CANCEL_MODAL_ENGAGED_GA4_EVENT,
  AI_CANCEL_MODAL_CANCEL_CLICKED_GA4_EVENT,
  AI_MODULE_OPENED_GA4_EVENT,
  AI_MODULE_CLOSED_GA4_EVENT,
  AI_MODULE_CLICKED_GA4_EVENT,
  AI_MODULE_VIEWED_GA4_EVENT,
  AI_MESSAGE_RECEIVED_GA4_EVENT,
  AI_ERROR_RESPONSE_GA4_EVENT,
  AI_MESSAGE_FAILURE_GA4_EVENT,
  AI_UNRECOGNIZED_INPUT_GA4_EVENT,
  AI_NAVIGATION_CLICKED_GA4_EVENT,
  AI_PAGE_VIEW_CHAT_HOME_GA4_EVENT,
  AI_PERSONA_SELECTED_GA4_EVENT,
  AI_RATE_LIMITED_GA4_EVENT,
  AI_MESSAGE_FEEDBACK_GA4_EVENT,
  AI_SPEECH_TO_TEXT_GA4_EVENT,
  AI_VIDEOGUIDE_ENABLED_GA4_EVENT,
  AI_VIDEOGUIDE_DISABLED_GA4_EVENT,
} from '../../../../services/event-tracking/index'

/**
 * Input method types for ai_message_sent event.
 */
export type AiInputMethod = 'Text' | 'Speech' | 'Prompt' | 'Card'

/**
 * Canonical, Title Case `input_method` param values (AI-820 / CS1).
 *
 * Single source of truth so every leg — PostHog here and the GA4 event-tracking
 * pipeline — emits identical values and can't drift per-callsite. Reference these
 * members instead of writing the string literals inline.
 */
export const AI_INPUT_METHOD = {
  TEXT: 'Text',
  SPEECH: 'Speech',
  PROMPT: 'Prompt',
  CARD: 'Card',
} as const satisfies Record<string, AiInputMethod>

/**
 * Submission method types for ai_message_sent event.
 */
export type AiSubmissionMethod = 'keyboard' | 'click'

/**
 * Named arguments for the `ai_message_sent` capture (AI-820). Replaces the
 * former positional list so the arbitrary argument ordering and the
 * `undefined` placeholder for skipped optionals disappear at the call site.
 */
export interface AiMessageSentArgs {
  message: string
  inputMethod: AiInputMethod
  uiElement: string
  submissionMethod?: AiSubmissionMethod
}

/**
 * Fan-out (`aiMessageSent`) variant: adds the optional cross-platform
 * `uiLocation`, emitted as `ui_location` on BOTH the PostHog and GA4 legs.
 */
export interface AiMessageSentFanoutArgs extends AiMessageSentArgs {
  uiLocation?: string
}

/**
 * Named arguments for `aiConversationStarted` / `aiConversationResumed`
 * (AI-820 PR2). Both `inputMethod` and `uiElement` are OPTIONAL because the
 * event has two distinct trigger types: a first/continuing message-send (which
 * carries both) and a New-button / persona-switch / silent-resume (which carry
 * neither). Each is emitted on BOTH legs only when present, so a trigger that
 * lacks them never ships an empty property.
 */
export interface AiConversationStartedArgs {
  inputMethod?: AiInputMethod
  uiElement?: string
}

export type AiConversationResumedArgs = AiConversationStartedArgs

/**
 * Named arguments for `aiCancelModalEngaged` (AI-820 PR2). Optional because the
 * GA4 leg historically carried `ui_element`/`input_method` that the PostHog leg
 * lacked; the fan-out now emits them on BOTH legs (parity). `ai_persona` is left
 * to each pipeline's default (GA4 middleware reads `oracle.persona`, PostHog
 * reads the chat-session context) rather than passed explicitly.
 */
export interface AiCancelModalEngagedArgs {
  inputMethod?: AiInputMethod
  uiElement?: string
}

/**
 * Named arguments for `aiModuleOpened` (AI-820 PR2). Was a 2-positional
 * `(uiElement, isInitial?)`. The GA4 leg historically omitted `ui_element`;
 * the fan-out now emits it on both legs.
 */
export interface AiModuleOpenedArgs {
  uiElement: string
  isInitial?: boolean
}

/**
 * Named arguments for `aiModuleClosed` (AI-820 PR2). Was a 3-positional
 * `(sessionDurationMs, uiElement, method?)`. GA4's two close paths previously
 * carried an inconsistent subset (one had `ui_element`+`method`, the other
 * `session_duration_ms`); the fan-out now emits all three uniformly on both legs.
 */
export interface AiModuleClosedArgs {
  sessionDurationMs: number | null
  uiElement: string
  method?: string
}

/**
 * Named arguments for `aiMessageReceived` (AI-820 PR2). Was a 3-positional
 * `(messagePairId, messageLength, inputMethod)`. Both legs now emit the same
 * payload — note the GA4 leg historically used `response_length`; the fan-out
 * unifies on the AI-768 canonical `message_length` (a GA4 param change flagged
 * for analytics sign-off).
 */
export interface AiMessageReceivedArgs {
  messagePairId: string
  messageLength: number
  inputMethod: AiInputMethod
}

/**
 * Named arguments shared by the three error/failure events
 * (`aiErrorResponse`, `aiMessageFailure`, `aiUnrecognizedInput`) — AI-820 PR2.
 * Was a 3-positional `(errorDetails, inputMethod, uiElement)`. Both legs now
 * emit identical props using the canonical `error_details` (the GA4 leg
 * historically used `error_message`/`error_code`; unifying on `error_details`
 * is a GA4 param change flagged for analytics sign-off).
 */
export interface AiErrorEventArgs {
  errorDetails: string
  inputMethod: AiInputMethod
  uiElement: string
}

/**
 * UI affordances for ai_message_feedback. Title Case per the AI-768
 * standardized vocabulary; backend `OracleThumbValue` ('up'/'down') is
 * mapped at the call site.
 */
export type AiFeedbackUiElement = 'Up' | 'Down'

/**
 * Catalog payload for ai_recommendation_viewed / _clicked. All eight
 * properties are spec-required; `null` is accepted for fields that the
 * backend cannot supply for the recommended item (e.g.,
 * `parent_series_id` for standalone media).
 */
export interface AiRecommendationPayload {
  ui_element: 'Cover Tile' | 'Hyperlink'
  content_id: string
  content_type: string
  content_name: string
  parent_series_id: string | null
  parent_series_name: string | null
  media_id: string | null
  media_language: string | null
  is_featured_media: boolean | null
}

/**
 * AI chat / conversation / video-guide events.
 * Fire from `lib/components/OracleSideGuide/`, `lib/components/OraclePage/`, and
 * related AI-conversation surfaces.
 */
export type ChatEvents = {
  aiCancelModalCancelClicked: () => void
  aiCancelModalEngaged: (args?: AiCancelModalEngagedArgs) => void
  aiCancelModalViewed: () => void
  // AI-820 PR2 fan-out keys (camelCase logical key → snake_case wire name on
  // both platforms). The zero-arg conversation events auto-attach
  // ui_element: 'Conversation List' on BOTH legs.
  aiConversationDeleted: () => void
  aiConversationFavorited: () => void
  aiConversationNameUpdated: () => void
  aiConversationOpened: () => void
  aiConversationResumed: (args?: AiConversationResumedArgs) => void
  aiConversationStarted: (args?: AiConversationStartedArgs) => void
  aiConversationUnfavorited: () => void
  aiErrorResponse: (args: AiErrorEventArgs) => void
  aiMessageFailure: (args: AiErrorEventArgs) => void
  aiMessageReceived: (args: AiMessageReceivedArgs) => void
  ai_message_regenerated: (uiElement: string) => void
  ai_message_sent: (args: AiMessageSentArgs) => void
  // AI-820 fan-out proof event. Same payload as the legacy `ai_message_sent`,
  // but one call fans out to BOTH platforms — the direct PostHog `.capture()`
  // leg AND the GA4 leg dispatched through the Redux event-tracking pipeline.
  // Takes the fan-out args (legacy base + optional `uiLocation`). The legacy
  // `ai_message_sent` key above stays PostHog-only for callsites pending
  // migration to this key.
  aiMessageSent: (args: AiMessageSentFanoutArgs) => void
  aiModuleClicked: (uiElement: string) => void
  aiModuleClosed: (args: AiModuleClosedArgs) => void
  aiModuleOpened: (args: AiModuleOpenedArgs) => void
  aiModuleViewed: (uiElement: string) => void
  aiNavigationClicked: () => void
  aiPageViewChatHome: () => void
  aiPersonaSelected: (aiPersona: string) => void
  // Optional `uiLocation` mirrors the GA4 dynamic ui_location ('AI Chat' vs
  // 'AI Voice Mode'); emitted on both legs when provided.
  aiRateLimited: (uiLocation?: string) => void
  // ai_recommendation_clicked / _viewed are NOT yet migrated to the fan-out
  // (payload-object events with a dual-source GA4 dispatch via a util) — they
  // remain PostHog-only keys here with their separate GA4 dispatch. Tracked as
  // a follow-up.
  ai_recommendation_clicked: (payload: AiRecommendationPayload) => void
  // PostHog leg emits `ui_element` ('Up'/'Down'); GA4 leg emits `content_rating`
  // ('up'/'down') derived from it — same signal, each platform's existing key.
  aiMessageFeedback: (uiElement: AiFeedbackUiElement) => void
  ai_recommendation_viewed: (payload: AiRecommendationPayload) => void
  aiSpeechToText: () => void
  aiUnrecognizedInput: (args: AiErrorEventArgs) => void
  aiVideoguideDisabled: () => void
  aiVideoguideEnabled: () => void
  concept_moment_toast_shown: (momentStartSeconds: number, conceptLabel: string) => void
  concept_moment_toast_clicked: (momentStartSeconds: number, conceptLabel: string) => void
  concept_moment_toast_dismissed: (momentStartSeconds: number, conceptLabel: string) => void
}

/**
 * Generic PostHog↔GA4 fan-out for a chat-domain event (AI-820 PR2). One call
 * dispatches the GA4 leg through the Redux event-tracking pipeline (middleware
 * then enriches ai_persona, app_name, gaia_uuid, …) AND fires the direct
 * PostHog `.capture()`. This is the single place that enforces the two
 * invariants every fan-out event shares:
 *   1. the GA4 leg is dispatched BEFORE the null-`posthogClient` guard, so an
 *      opt-out / test-time null client never suppresses the GA4 leg;
 *   2. the PostHog payload always carries the shared `buildInChatContextProperties`
 *      auto-attach block.
 * `ga4Props` / `phProps` are the per-event extras; the GA4 leg merges them into
 * the base Immutable event, the PostHog leg spreads them after the auto-attach.
 */
function fanOutChatEvent (
  ctx: PostHogCallbackContext,
  {
    ga4Event, ga4Props, phEvent, phProps,
  }: {
    ga4Event: typeof AI_MESSAGE_SENT_GA4_EVENT
    ga4Props?: Record<string, unknown>
    phEvent: string
    phProps?: Record<string, unknown>
  },
): void {
  ctx.dispatch(setAiGa4Event(ga4Props ? ga4Event.mergeDeep(ga4Props) : ga4Event))
  if (!ctx.posthogClient) return
  ctx.posthogClient.capture(phEvent, {
    ...buildInChatContextProperties(ctx),
    ...phProps,
  })
}

/**
 * Per-event PostHog props for `ai_message_sent` (excludes the shared auto-attach
 * block, which `fanOutChatEvent` / the legacy callback add). Single point of
 * definition shared by the legacy PostHog-only `ai_message_sent` key and the
 * fan-out `aiMessageSent` key so the two cannot drift.
 */
function aiMessageSentPhProps (
  {
    message, inputMethod, uiElement, submissionMethod, uiLocation,
  }: AiMessageSentFanoutArgs,
): Record<string, unknown> {
  return {
    message_length: Array.from(message).length,
    input_method: inputMethod,
    ui_element: uiElement,
    ...(submissionMethod && { submission_method: submissionMethod }),
    ...(uiLocation && { ui_location: uiLocation }),
  }
}

export function createChatEventCallbacks (
  contextRef: PostHogContextRef,
): ChatEvents {
  return {
    aiCancelModalCancelClicked: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CANCEL_MODAL_CANCEL_CLICKED_GA4_EVENT,
        phEvent: 'ai_cancel_modal_cancel_clicked',
      })
    },

    aiCancelModalEngaged: (args?: AiCancelModalEngagedArgs) => {
      const props = {
        ...(args?.inputMethod && { input_method: args.inputMethod }),
        ...(args?.uiElement && { ui_element: args.uiElement }),
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CANCEL_MODAL_ENGAGED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_cancel_modal_engaged',
        phProps: props,
      })
    },

    aiCancelModalViewed: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CANCEL_MODAL_VIEWED_GA4_EVENT,
        phEvent: 'ai_cancel_modal_viewed',
      })
    },

    aiConversationDeleted: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_DELETED_GA4_EVENT,
        ga4Props: { ui_element: 'Conversation List' },
        phEvent: 'ai_conversation_deleted',
        phProps: { ui_element: 'Conversation List' },
      })
    },

    aiConversationFavorited: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_FAVORITED_GA4_EVENT,
        ga4Props: { ui_element: 'Conversation List' },
        phEvent: 'ai_conversation_favorited',
        phProps: { ui_element: 'Conversation List' },
      })
    },

    aiConversationNameUpdated: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_NAME_UPDATED_GA4_EVENT,
        ga4Props: { ui_element: 'Conversation List' },
        phEvent: 'ai_conversation_name_updated',
        phProps: { ui_element: 'Conversation List' },
      })
    },

    // GA4 historically dispatched `ai_conversation_opened` with no props; the
    // fan-out now emits `ui_element: 'Conversation List'` on the GA4 leg too,
    // matching the PostHog leg (parity gap-fill).
    aiConversationOpened: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_OPENED_GA4_EVENT,
        ga4Props: { ui_element: 'Conversation List' },
        phEvent: 'ai_conversation_opened',
        phProps: { ui_element: 'Conversation List' },
      })
    },

    aiConversationResumed: (args?: AiConversationResumedArgs) => {
      const props = {
        ...(args?.inputMethod && { input_method: args.inputMethod }),
        ...(args?.uiElement && { ui_element: args.uiElement }),
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_RESUMED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_conversation_resumed',
        phProps: props,
      })
    },

    aiConversationStarted: (args?: AiConversationStartedArgs) => {
      const props = {
        ...(args?.inputMethod && { input_method: args.inputMethod }),
        ...(args?.uiElement && { ui_element: args.uiElement }),
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_STARTED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_conversation_started',
        phProps: props,
      })
    },

    aiConversationUnfavorited: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_CONVERSATION_UNFAVORITED_GA4_EVENT,
        ga4Props: { ui_element: 'Conversation List' },
        phEvent: 'ai_conversation_unfavorited',
        phProps: { ui_element: 'Conversation List' },
      })
    },

    aiErrorResponse: ({ errorDetails, inputMethod, uiElement }: AiErrorEventArgs) => {
      const props = {
        error_details: errorDetails,
        input_method: inputMethod,
        ui_element: uiElement,
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_ERROR_RESPONSE_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_error_response',
        phProps: props,
      })
    },

    aiMessageFailure: ({ errorDetails, inputMethod, uiElement }: AiErrorEventArgs) => {
      const props = {
        error_details: errorDetails,
        input_method: inputMethod,
        ui_element: uiElement,
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MESSAGE_FAILURE_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_message_failure',
        phProps: props,
      })
    },

    aiMessageReceived: ({ messagePairId, messageLength, inputMethod }: AiMessageReceivedArgs) => {
      const props = {
        message_pair_id: messagePairId,
        message_length: messageLength,
        input_method: inputMethod,
        ui_element: 'Text',
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MESSAGE_RECEIVED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_message_received',
        phProps: props,
      })
    },

    ai_message_sent: (args: AiMessageSentArgs) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_message_sent', {
        ...buildInChatContextProperties(ctx),
        ...aiMessageSentPhProps(args),
      })
    },

    // AI-820 fan-out proof event: one logical call → both platforms via the
    // shared `fanOutChatEvent` helper. The PostHog payload shares
    // `aiMessageSentPhProps` with the legacy `ai_message_sent` callback (which
    // remains for PostHog-only callsites pending migration to this camelCase key).
    aiMessageSent: (args: AiMessageSentFanoutArgs) => {
      const { inputMethod, uiElement, uiLocation } = args
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MESSAGE_SENT_GA4_EVENT,
        ga4Props: {
          ui_element: uiElement,
          input_method: inputMethod,
          ...(uiLocation && { ui_location: uiLocation }),
        },
        phEvent: 'ai_message_sent',
        phProps: aiMessageSentPhProps(args),
      })
    },

    aiModuleClicked: (uiElement: string) => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MODULE_CLICKED_GA4_EVENT,
        ga4Props: { ui_element: uiElement },
        phEvent: 'ai_module_clicked',
        phProps: { ui_element: uiElement },
      })
    },

    aiModuleClosed: ({ sessionDurationMs, uiElement, method }: AiModuleClosedArgs) => {
      const props = {
        session_duration_ms: sessionDurationMs,
        ui_element: uiElement,
        ...(method && { method }),
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MODULE_CLOSED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_module_closed',
        phProps: props,
      })
    },

    aiModuleOpened: ({ uiElement, isInitial }: AiModuleOpenedArgs) => {
      const props = {
        ui_element: uiElement,
        ...(isInitial !== undefined && { is_initial: isInitial }),
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MODULE_OPENED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_module_opened',
        phProps: props,
      })
    },

    aiModuleViewed: (uiElement: string) => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MODULE_VIEWED_GA4_EVENT,
        ga4Props: { ui_element: uiElement },
        phEvent: 'ai_module_viewed',
        phProps: { ui_element: uiElement },
      })
    },

    aiNavigationClicked: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_NAVIGATION_CLICKED_GA4_EVENT,
        phEvent: 'ai_navigation_clicked',
      })
    },

    aiPageViewChatHome: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_PAGE_VIEW_CHAT_HOME_GA4_EVENT,
        phEvent: 'ai_page_view_chat_home',
      })
    },

    aiPersonaSelected: (aiPersona: string) => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_PERSONA_SELECTED_GA4_EVENT,
        ga4Props: { ai_persona: aiPersona },
        phEvent: 'ai_persona_selected',
        phProps: { ai_persona: aiPersona },
      })
    },

    aiRateLimited: (uiLocation?: string) => {
      const props = uiLocation ? { ui_location: uiLocation } : undefined
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_RATE_LIMITED_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_rate_limited',
        phProps: props,
      })
    },

    ai_recommendation_clicked: (payload: AiRecommendationPayload) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_recommendation_clicked', {
        ...buildInChatContextProperties(ctx),
        ...payload,
      })
    },

    ai_recommendation_viewed: (payload: AiRecommendationPayload) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_recommendation_viewed', {
        ...buildInChatContextProperties(ctx),
        ...payload,
      })
    },

    ai_message_regenerated: (uiElement: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_message_regenerated', {
        ...buildInChatContextProperties(ctx),
        input_method: AI_INPUT_METHOD.PROMPT,
        ui_element: uiElement,
      })
    },

    // Param fork: the thumb direction is sent as `ui_element` ('Up'/'Down') to
    // PostHog (existing) and as `content_rating` ('up'/'down') to GA4 (existing),
    // derived from the one arg. Same signal, each platform's own key.
    aiMessageFeedback: (uiElement: AiFeedbackUiElement) => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_MESSAGE_FEEDBACK_GA4_EVENT,
        ga4Props: { content_rating: uiElement.toLowerCase() },
        phEvent: 'ai_message_feedback',
        phProps: { ui_element: uiElement },
      })
    },

    aiSpeechToText: () => {
      // GA4 historically dispatched this bare; the fan-out now gives it the same
      // input_method + ui_element the PostHog leg carries (parity fill).
      const props = {
        input_method: AI_INPUT_METHOD.SPEECH,
        ui_element: 'Voice Mode',
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_SPEECH_TO_TEXT_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_speech_to_text',
        phProps: props,
      })
    },

    aiUnrecognizedInput: ({ errorDetails, inputMethod, uiElement }: AiErrorEventArgs) => {
      const props = {
        error_details: errorDetails,
        input_method: inputMethod,
        ui_element: uiElement,
      }
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_UNRECOGNIZED_INPUT_GA4_EVENT,
        ga4Props: props,
        phEvent: 'ai_unrecognized_input',
        phProps: props,
      })
    },

    aiVideoguideDisabled: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_VIDEOGUIDE_DISABLED_GA4_EVENT,
        phEvent: 'ai_videoguide_disabled',
      })
    },

    aiVideoguideEnabled: () => {
      fanOutChatEvent(contextRef.current, {
        ga4Event: AI_VIDEOGUIDE_ENABLED_GA4_EVENT,
        phEvent: 'ai_videoguide_enabled',
      })
    },

    concept_moment_toast_shown: (momentStartSeconds: number, conceptLabel: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('concept_moment_toast_shown', {
        ...buildInChatContextProperties(ctx),
        moment_start_seconds: momentStartSeconds,
        concept_label: conceptLabel,
      })
    },

    concept_moment_toast_clicked: (momentStartSeconds: number, conceptLabel: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('concept_moment_toast_clicked', {
        ...buildInChatContextProperties(ctx),
        moment_start_seconds: momentStartSeconds,
        concept_label: conceptLabel,
      })
    },

    concept_moment_toast_dismissed: (momentStartSeconds: number, conceptLabel: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('concept_moment_toast_dismissed', {
        ...buildInChatContextProperties(ctx),
        moment_start_seconds: momentStartSeconds,
        concept_label: conceptLabel,
      })
    },
  }
}
